const isRecord = (
	value: unknown
): value is Record<string, any> => {
	return typeof value === "object" && value !== null;
};

const isRawProbNode = (
	value: unknown
): value is Record<string, any> => {
	if (!isRecord(value)) {
		return false;
	}

	return (
		isRecord(value.bucket_grouping) ||
		Array.isArray(value.probs)
	);
};

export const getRawProbDataTree = (
	response: unknown
): Record<string, any> | null => {
	const candidates = [
		isRecord(response) && isRecord(response.output)
			? response.output.raw_probs_data
			: null,
		isRecord(response) ? response.raw_probs_data : null,
		isRecord(response) ? response.output : null,
		response,
	];

	for (const candidate of candidates) {
		if (isRawProbNode(candidate)) {
			return candidate;
		}
	}

	return null;
};

export const getRawProbStatus = (response: unknown) => {
	if (!isRecord(response)) {
		return "";
	}

	return String(response.status ?? "")
		.trim()
		.toUpperCase();
};

export const areAllRawProbabilitiesConfirmed = (
	response: unknown
) => {
	const rawProbTree = getRawProbDataTree(response);
	if (!rawProbTree) {
		return false;
	}

	const walkTree = (node: Record<string, any>): boolean => {
		if (
			typeof node.is_confirmed === "boolean" &&
			!node.is_confirmed
		) {
			return false;
		}

		if (isRecord(node.bucket_grouping)) {
			return Object.values(node.bucket_grouping).every(
				(childNode) =>
					isRecord(childNode) && walkTree(childNode)
			);
		}

		return true;
	};

	return walkTree(rawProbTree);
};

export const extractMatrix = (data: any) => {
	const res = {};
	const buckets = {};
	const bucket_mapping = {};
	goToLeaf(data, "root", res, buckets, bucket_mapping);
	return {
		lookUp: res,
		buckets,
		bucket_mapping,
	};
};

const goToLeaf = (
	node: any,
	currPath: string,
	res: any,
	buckets: any,
	bucket_mapping: any
	// eslint-disable-next-line max-params
) => {
	if (node.bucket_grouping) {
		Object.keys(node.bucket_grouping).forEach((key) => {
			const rf = node.rf_id;
			if (!bucket_mapping[rf]) {
				bucket_mapping[rf] = node.rf_display_name;
			}
			const newPath = currPath + " -> " + `${rf} -> ` + key;
			const child = node.bucket_grouping[key];

			if (buckets[rf]) {
				if (!buckets[rf].includes(key)) {
					buckets[rf].push(key);
				}
			} else {
				buckets[rf] = [key];
			}
			goToLeaf(
				child,
				newPath,
				res,
				buckets,
				bucket_mapping
			);
		});
	} else {
		res[currPath] = node;
	}
};
